import React, { useEffect, useState } from "react";
import { useSession } from "../lib/session";
import { listCourses, createCourse, getCourse, updateCourse, upsertTeeSet, deleteTeeSet } from "../lib/api";
import { Field, Notice, Empty, PageHead, Loading } from "../components/ui";
import Back from "../components/Back";
import HoleGrid from "../components/HoleGrid";

/**
 * Courses.
 *
 * Entering par and stroke index for 18 holes is tedious, and it's the one
 * place a mistake corrupts every score on the course forever. So it's
 * paid once per venue and then reused, and duplicate stroke indexes block
 * saving rather than being quietly accepted.
 *
 * Ladies' tees get their own par and stroke index, which is why each tee
 * set carries its own 18 holes rather than sharing one card.
 */

const blank = () =>
  Array.from({ length: 18 }, (_, i) => ({ hole_number: i + 1, par: 4, stroke_index: i + 1 }));

const emptyTee = (name, gender) => ({
  name, gender, par: 72, course_rating: "", slope_rating: "", holes: blank() });


export default function Courses() {
  const { society, isOrganiser } = useSession();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", club: "", city: "" });
  const [tees, setTees] = useState([emptyTee("Yellow", "male")]);
  const [open, setOpen] = useState(0);
  const [editing, setEditing] = useState(null);   // full course being corrected

  async function startEdit(courseId) {
    setError("");
    try {
      const c = await getCourse(courseId);
      setEditing(c);
      setForm({ name: c.name, club: c.club_name ?? "", city: c.city ?? "" });
      setTees((c.tee_sets ?? []).map((t) => ({
        id: t.id,
        name: t.name,
        gender: t.gender,
        course_rating: t.course_rating,
        slope_rating: t.slope_rating,
        holes: [...(t.tee_holes ?? [])]
          .sort((a, b) => a.hole_number - b.hole_number)
          .map((h) => ({ hole_number: h.hole_number, par: h.par, stroke_index: h.stroke_index })) })));
      setOpen(-1);
    } catch (e) { setError(e.message); }
  }

  async function saveEdit(e) {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await updateCourse(editing.id, form);
      for (const t of tees) {
        await upsertTeeSet(editing.id, t.id ?? null, t);
      }
      const removed = (editing.tee_sets ?? [])
        .filter((old) => !tees.some((t) => t.id === old.id));
      for (const r of removed) await deleteTeeSet(r.id);

      setEditing(null);
      setForm({ name: "", club: "", city: "" });
      setTees([emptyTee("Yellow", "male")]);
      await load();
    } catch (err) {
      setError(err.message ?? String(err));
    } finally { setBusy(false); }
  }

  function cancel() {
    setAdding(false); setEditing(null);
    setForm({ name: "", club: "", city: "" });
    setTees([emptyTee("Yellow", "male")]);
    setOpen(0);
  }

  const load = () => listCourses(society.id).then(setRows).catch((e) => setError(e.message));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [society.id]);

  const valid =
    form.name.trim().length > 1 &&
    tees.length > 0 &&
    tees.every((t) =>
      t.name.trim() &&
      Number(t.course_rating) > 0 &&
      Number(t.slope_rating) >= 55 &&
      new Set(t.holes.map((h) => h.stroke_index)).size === 18);

  async function submit(e) {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await createCourse(society.id, {
        ...form,
        tees: tees.map((t) => ({
          ...t,
          par: t.holes.reduce((a, h) => a + Number(h.par), 0),
          course_rating: Number(t.course_rating),
          slope_rating: Number(t.slope_rating) })) });
      setAdding(false);
      setForm({ name: "", club: "", city: "" });
      setTees([emptyTee("Yellow", "male")]);
      await load();
    } catch (err) {
      setError(err.message ?? String(err));
    } finally { setBusy(false); }
  }

  if (!rows) return <Loading />;

  if (adding || editing) {
    return (
      <form onSubmit={editing ? saveEdit : submit}>
      <Back to="/society">Society</Back>
        <PageHead
          title={editing ? `Edit ${editing.name}` : "Add a course"}
          sub={editing
            ? "Correcting a stroke index re-works every nett and Stableford score on this course."
            : "Copy par and stroke index off a scorecard. Once per venue, then reusable."} />
        <Notice kind="error">{error}</Notice>

        <div className="surface" style={{ padding: "1.1rem", marginBottom: "1rem" }}>
          <Field label="Course name">
            <input className="input" required autoFocus value={form.name}
                   onChange={(e) => setForm({ ...form, name: e.target.value })}
                   placeholder="Long Ashton" />
          </Field>
          <div className="grid2">
            <Field label="Club"><input className="input" value={form.club}
              onChange={(e) => setForm({ ...form, club: e.target.value })}
              placeholder="Long Ashton Golf Club" /></Field>
            <Field label="Town"><input className="input" value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              placeholder="Bristol" /></Field>
          </div>
        </div>

        {tees.map((t, ti) => (
          <div className="surface" key={ti} style={{ padding: "1.1rem", marginBottom: "1rem" }}>
            <div className="row" style={{ marginBottom: ".8rem" }}>
              <strong style={{ fontSize: "1.15rem",
                               textTransform: "uppercase" }}>
                {t.name || "Tees"} · {t.gender === "female" ? "Ladies'" : "Men's"}
              </strong>
              <span className="spacer" />
              <button type="button" className="btn-link"
                      onClick={() => setOpen(open === ti ? -1 : ti)}>
                {open === ti ? "Hide holes" : "Edit holes"}
              </button>
              {tees.length > 1 && (
                <button type="button" className="btn-danger"
                        onClick={() => setTees(tees.filter((_, k) => k !== ti))}>
                  Remove
                </button>
              )}
            </div>

            <div className="grid2">
              <Field label="Tee name">
                <input className="input" required value={t.name}
                       onChange={(e) => setTees(tees.map((x, k) =>
                         k === ti ? { ...x, name: e.target.value } : x))} />
              </Field>
              <Field label="Played by">
                <select className="select" value={t.gender}
                        onChange={(e) => setTees(tees.map((x, k) =>
                          k === ti ? { ...x, gender: e.target.value } : x))}>
                  <option value="male">Men</option>
                  <option value="female">Ladies</option>
                  <option value="any">Anyone</option>
                </select>
              </Field>
            </div>
            <div className="grid2">
              <Field label="Course rating" hint="On the scorecard, e.g. 69.2">
                <input className="input" required inputMode="decimal" value={t.course_rating}
                       onChange={(e) => setTees(tees.map((x, k) =>
                         k === ti ? { ...x, course_rating: e.target.value } : x))}
                       placeholder="69.2" />
              </Field>
              <Field label="Slope rating" hint="Whole number, 55 to 155">
                <input className="input" required inputMode="numeric" value={t.slope_rating}
                       onChange={(e) => setTees(tees.map((x, k) =>
                         k === ti ? { ...x, slope_rating: e.target.value } : x))}
                       placeholder="125" />
              </Field>
            </div>

            {open === ti && (
              <HoleGrid tee={t}
                        onChange={(next) => setTees(tees.map((x, k) => (k === ti ? next : x)))} />
            )}
          </div>
        ))}

        <button type="button" className="btn btn-ghost btn-block"
                onClick={() => {
                  setTees([...tees, emptyTee("Red", "female")]);
                  setOpen(tees.length);
                }}>
          Add another set of tees
        </button>

        <p className="muted" style={{ fontSize: ".75rem", margin: ".8rem 0 1rem", lineHeight: 1.6 }}>
          Add ladies' tees if any of your members play them. They have their
          own par and stroke index, and course handicap makes the nett scores
          comparable across both.
        </p>

        <div className="row">
          <button className="btn btn-primary" disabled={busy || !valid}>
            {busy ? "Saving…" : editing ? "Save changes" : "Save course"}
          </button>
          <button type="button" className="btn btn-ghost" onClick={cancel}>
            Cancel
          </button>
        </div>
      </form>
    );
  }

  return (
    <>
      <Back to="/society">Society</Back>
      <PageHead title="Courses" sub={`${rows.length} saved`}>
        {isOrganiser && (
          <button className="btn btn-primary" onClick={() => setAdding(true)}>Add course</button>
        )}
      </PageHead>

      <Notice kind="error">{error}</Notice>

      {rows.length === 0 ? (
        <Empty title="No courses yet"
               action={isOrganiser && (
                 <button className="btn btn-primary" onClick={() => setAdding(true)}>
                   Add your first course
                 </button>
               )}>
          You'll need at least one before you can set up a competition.
          Have a scorecard to hand — you need par and stroke index for all
          18 holes, plus the course and slope ratings.
        </Empty>
      ) : (
        <div className="list">
          {rows.map((c) => (
            <button className="list-row" key={c.id}
                    onClick={() => isOrganiser && c.society_id && startEdit(c.id)}
                    disabled={!isOrganiser || !c.society_id}
                    style={{ border: "none", borderBottom: "1px solid var(--line)",
                             background: "none", textAlign: "left", font: "inherit",
                             cursor: isOrganiser && c.society_id ? "pointer" : "default" }}>
              <span style={{ minWidth: 0 }}>
                <strong style={{ display: "block" }}>{c.name}</strong>
                <small className="muted" style={{ fontSize: ".75rem" }}>
                  {[c.club_name, c.city].filter(Boolean).join(" · ") || "no club recorded"}
                </small>
              </span>
              <span className="muted" style={{ fontSize: ".8125rem", textAlign: "right" }}>
                {(c.tee_sets ?? []).map((t) => t.name).join(", ") || "no tees"}
              </span>
            </button>
          ))}
        </div>
      )}

      {isOrganiser && rows.length > 0 && (
        <p className="muted" style={{ fontSize: ".75rem", marginTop: ".8rem", lineHeight: 1.6 }}>
          Tap a course to correct it. Tees already used on a card can't be
          deleted, but their par and stroke index can be fixed — and every
          score on that course is re-worked from the corrected card.
        </p>
      )}
    </>
  );
}
